import { DISPLAY } from '../game/config';
import type { Camera, Vec2, Vec3 } from '../game/models';

export interface Viewport {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type CameraPoint = Vec3;

export interface ProjectedSegment {
  a: Vec2;
  b: Vec2;
  depth: number;
}

export const ACTION_VIEWPORT: Viewport = {
  x: 0,
  y: DISPLAY.hudHeight,
  width: DISPLAY.width,
  height: DISPLAY.height - DISPLAY.hudHeight,
};

export function createGroundGrid(cameraPosition: Vec3, spacing = 10): ReadonlyArray<readonly [Vec3, Vec3]> {
  const centerX = Math.floor(cameraPosition.x / spacing) * spacing;
  const centerZ = Math.floor(cameraPosition.z / spacing) * spacing;
  const extent = Math.ceil((DISPLAY.far + spacing) / spacing) * spacing;
  const segments: Array<readonly [Vec3, Vec3]> = [];
  for (let offset = -extent; offset <= extent; offset += spacing) {
    segments.push([
      { x: centerX + offset, y: 0, z: centerZ - extent },
      { x: centerX + offset, y: 0, z: centerZ + extent },
    ]);
    segments.push([
      { x: centerX - extent, y: 0, z: centerZ + offset },
      { x: centerX + extent, y: 0, z: centerZ + offset },
    ]);
  }
  return segments;
}

export function worldToCamera(point: Vec3, camera: Camera): CameraPoint {
  const dx = point.x - camera.position.x;
  const dy = point.y - camera.position.y;
  const dz = point.z - camera.position.z;
  const cosine = Math.cos(camera.yaw);
  const sine = Math.sin(camera.yaw);
  return {
    x: dx * cosine + dz * sine,
    y: dy,
    z: -dx * sine + dz * cosine,
  };
}

export function clipToNearPlane(
  start: CameraPoint,
  end: CameraPoint,
  near = DISPLAY.near,
): [CameraPoint, CameraPoint] | null {
  if (start.z < near && end.z < near) return null;
  if (start.z >= near && end.z >= near) return [start, end];

  const ratio = (near - start.z) / (end.z - start.z);
  const crossing: CameraPoint = {
    x: start.x + (end.x - start.x) * ratio,
    y: start.y + (end.y - start.y) * ratio,
    z: near,
  };
  return start.z < near ? [crossing, end] : [start, crossing];
}

export function clipToFarPlane(
  start: CameraPoint,
  end: CameraPoint,
  far = DISPLAY.far,
): [CameraPoint, CameraPoint] | null {
  if (start.z > far && end.z > far) return null;
  if (start.z <= far && end.z <= far) return [start, end];

  const ratio = (far - start.z) / (end.z - start.z);
  const crossing: CameraPoint = {
    x: start.x + (end.x - start.x) * ratio,
    y: start.y + (end.y - start.y) * ratio,
    z: far,
  };
  return start.z > far ? [crossing, end] : [start, crossing];
}

export function projectCameraPoint(point: CameraPoint, viewport = ACTION_VIEWPORT): Vec2 {
  const focal = viewport.width / (2 * Math.tan(DISPLAY.horizontalFovRadians / 2));
  return {
    x: viewport.x + viewport.width / 2 + (point.x / point.z) * focal,
    y: viewport.y + viewport.height / 2 - (point.y / point.z) * focal,
  };
}

export function clipLineToViewport(start: Vec2, end: Vec2, viewport = ACTION_VIEWPORT): [Vec2, Vec2] | null {
  const minX = viewport.x;
  const maxX = viewport.x + viewport.width;
  const minY = viewport.y;
  const maxY = viewport.y + viewport.height;
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  let first = 0;
  let last = 1;
  const edges: readonly [number, number][] = [
    [-dx, start.x - minX],
    [dx, maxX - start.x],
    [-dy, start.y - minY],
    [dy, maxY - start.y],
  ];

  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) return null;
      continue;
    }
    const ratio = q / p;
    if (p < 0) first = Math.max(first, ratio);
    else last = Math.min(last, ratio);
    if (first > last) return null;
  }

  return [
    { x: start.x + first * dx, y: start.y + first * dy },
    { x: start.x + last * dx, y: start.y + last * dy },
  ];
}

export function projectWorldSegment(
  start: Vec3,
  end: Vec3,
  camera: Camera,
  viewport = ACTION_VIEWPORT,
): ProjectedSegment | null {
  const cameraStart = worldToCamera(start, camera);
  const cameraEnd = worldToCamera(end, camera);
  const clippedNear = clipToNearPlane(cameraStart, cameraEnd);
  if (!clippedNear) return null;
  const clippedDepth = clipToFarPlane(clippedNear[0], clippedNear[1]);
  if (!clippedDepth) return null;

  const projectedStart = projectCameraPoint(clippedDepth[0], viewport);
  const projectedEnd = projectCameraPoint(clippedDepth[1], viewport);
  const clippedViewport = clipLineToViewport(projectedStart, projectedEnd, viewport);
  if (!clippedViewport) return null;

  return {
    a: clippedViewport[0],
    b: clippedViewport[1],
    depth: (clippedDepth[0].z + clippedDepth[1].z) / 2,
  };
}
